// Schemas Zod e tipos da API de cartões (7.11).
import { z } from 'zod';
import type { CardState, CardTemplate, NoteType } from '../types';
import type { AnswerMode, IntervalPreview, LevelStatus } from '../rules/cards';
import type { ImportStatus } from '../rules/card-import';

export const cardsLanguageQuerySchema = z.object({ language: z.string().min(2).max(8) });
export const cardsQueueQuerySchema = z.object({
  language: z.string().min(2).max(8),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});

export const reviewSessionCreateSchema = z.object({ languageId: z.string().uuid() });
export const reviewSessionPatchSchema = z.object({
  activeSeconds: z.number().int().min(0).max(36_000),
  ended: z.boolean().optional(),
});

export const reviewCreateSchema = z.object({
  clientId: z.string().uuid(),
  reviewSessionId: z.string().uuid().optional().nullable(),
  rating: z.number().int().min(1).max(4),
  typedAnswer: z.string().max(200).optional().nullable(),
  autoCorrect: z.boolean().optional().nullable(),
  answerMs: z.number().int().min(0).max(3_600_000).optional().nullable(),
  /** tempo ativo acumulado da sessão (o PATCH continua valendo a cada 60 s) */
  activeSeconds: z.number().int().min(0).max(36_000).optional(),
});
export type ReviewCreate = z.infer<typeof reviewCreateSchema>;

export const notesQuerySchema = z.object({
  deck: z.string().uuid(),
  q: z.string().max(100).optional(),
  status: z.enum(['novo', 'aprendendo', 'aprendido', 'esperando', 'suspenso']).optional(),
});
export const noteCreateSchema = z.object({ fields: z.record(z.unknown()), audioUrl: z.string().url().optional().nullable() });
export const notePatchSchema = z.object({
  fields: z.record(z.unknown()).optional(),
  audioUrl: z.string().url().optional().nullable(),
});
export const deckCreateSchema = z.object({
  levelId: z.string().uuid(),
  name: z.string().min(1).max(60),
  noteType: z.enum(['kana', 'kanji', 'vocab_ja', 'vocab']).optional(),
});
export const importBodySchema = z.object({ text: z.string().max(2_000_000), filename: z.string().max(200).optional().nullable() });
export const cardSettingsPatchSchema = z.object({
  newPerDay: z.number().int().min(0).max(100).optional(),
  maxReviewsPerDay: z.number().int().min(10).max(2000).optional(),
  desiredRetention: z.number().min(0.7).max(0.99).optional(),
  maxIntervalDays: z.number().int().min(7).max(36500).optional(),
  learningSteps: z.string().max(40).optional(),
  relearningSteps: z.string().max(40).optional(),
  leechLapses: z.number().int().min(2).max(50).optional(),
  audioOnReveal: z.boolean().optional(),
  furiganaDefault: z.boolean().optional(),
});
export type CardSettingsPatch = z.infer<typeof cardSettingsPatchSchema>;

export type CardSettings = {
  languageId: string;
  newPerDay: number;
  maxReviewsPerDay: number;
  desiredRetention: number;
  maxIntervalDays: number;
  learningSteps: string;
  relearningSteps: string;
  leechLapses: number;
  audioOnReveal: boolean;
  furiganaDefault: boolean;
};

export type DeckInfo = { id: string; code: string; name: string; noteType: NoteType; sort: number; levelId: string; items: number };

export type LevelInfo = {
  id: string;
  code: string;
  name: string;
  badge: string;
  sort: number;
  unlockPct: number;
  unlocked: boolean;
  total: number;
  learned: number;
  /** já vistos, ainda não aprendidos (intervalo < 7 dias) */
  started: number;
  pct: number;
  /** % de aprendidos + em estudo (barra clara atrás da cheia) */
  startedPct: number;
  status: LevelStatus;
  /** "104 caracteres · 90 aprendidos", "Kanji 25 importados", "Nenhum cartão ainda" */
  detail: string;
  /** "abre com 80% do N5" (fechado) */
  lockText: string | null;
  decks: DeckInfo[];
};

export type LeechInfo = { cardId: string; noteId: string; key: string; template: CardTemplate; side: string; lapses: number; deckName: string };

export type CardsTab = { code: string; kanji: string; name: string; due: number; new: number };

export type CardsOverview = {
  language: { id: string; code: string; name: string; kanji: string; nativeName: string };
  tabs: CardsTab[];
  due: number;
  new: number;
  estMinutes: number;
  avgSeconds: number;
  today: { reviews: number; minutes: number; otherMinutes: number; text: string };
  learned: number;
  /** itens já vistos que ainda não chegaram a aprendido */
  started: number;
  accuracy: number | null;
  levels: LevelInfo[];
  rule: string;
  next7: { date: string; label: string; count: number }[];
  settings: CardSettings;
  leeches: LeechInfo[];
};

export type QueueItem = {
  cardId: string;
  noteId: string;
  template: CardTemplate;
  side: 1 | 2;
  sideLabel: string; // "Ler"
  mode: AnswerMode;
  state: CardState;
  isNew: boolean;
  noteType: NoteType;
  deckName: string;
  levelCode: string;
  fields: Record<string, unknown>;
  audioUrl: string | null;
  /** pergunta do lado Escrever ("ji · linha d") */
  prompt: string | null;
  /** 4 opções do lado Escrever */
  choices: string[] | null;
  /** kanji da palavra com significados (verso do Ler/Lembrar) */
  kanjiInfo: { kanji: string; meanings: string[] }[];
  /** kana: o par do outro silabário e os parecidos */
  counterpart: string | null;
  similar: { char: string; romaji: string }[];
  intervals: IntervalPreview[];
};

export type CardsQueue = {
  language: { id: string; code: string; name: string; kanji: string };
  items: QueueItem[];
  total: number; // para revisar + novos no começo
  done: number; // revisões feitas hoje
  todayMinutes: number; // minutos de estudo do idioma hoje
  goal: number;
  audioOnReveal: boolean;
  furiganaDefault: boolean;
};

export type ReviewSessionInfo = { id: string; languageId: string; date: string; activeSeconds: number; reviews: number; studySessionId: string | null };

export type ReviewResult = {
  logId: string;
  cardId: string;
  state: CardState;
  due: string | null;
  scheduledDays: number;
  suspended: boolean;
  buriedCardId: string | null;
  unlockedLevels: { id: string; name: string }[];
  session: ReviewSessionInfo | null;
};

export type ImportPreviewRow = {
  line: number;
  key: string;
  status: ImportStatus;
  kanji?: string[];
  reason?: string;
  a: string;
  b: string;
  c: string;
};

export type ImportPreview = {
  deck: { id: string; name: string; noteType: NoteType };
  rows: ImportPreviewRow[];
  summary: Record<ImportStatus, number> & { total: number; items: number; cards: number };
  summaryText: string;
};

export type ImportResult = {
  batchId: string;
  created: number;
  duplicates: number;
  invalid: number;
  deckName: string;
  firstPosition: number | null;
  lastPosition: number | null;
  /** posição do primeiro item novo na fila de novos do idioma (1 = o próximo a entrar) */
  queuePlace: number | null;
  newPerDay: number;
};

export type NoteListItem = {
  id: string;
  key: string;
  fields: Record<string, unknown>;
  position: number;
  source: string;
  status: 'novo' | 'aprendendo' | 'aprendido' | 'esperando' | 'suspenso';
  cards: { id: string; template: CardTemplate; state: CardState; due: string | null; suspended: boolean; lapses: number }[];
};
